import { inject, injectable } from 'inversify';
import { DEFAULT_AUDIO_ENCODINGS } from '../../constants/encodings';
import { MEDIASOUP_EVENTS } from '../../constants/events';
import { TOKEN } from '../../inversify.tokens';
import type { PeerVolume } from '../../types/channelAudioObserver';
import {
  type ProducerData,
  TrackLabel,
} from '../../types/common';
import Logger from '../Logger';
import PeersStorage from '../PeersStorage';

@injectable()
class ChannelAudioObserverMissingProducerRestorer {
  readonly #logger: Logger;

  constructor(
    @inject(TOKEN.PeersStorage) private readonly peersStorage: PeersStorage,
    @inject(TOKEN.LoggerFactory) loggerFactory: (namespace: string) => Logger,
  ) {
    this.#logger = loggerFactory('ChannelAudioObserverMissingProducerRestorer');
  }

  public handlePeerVolume(volume: PeerVolume): void {
    const { peerId, producerId } = volume;
    const peer = this.peersStorage.get(peerId);

    if (!peer) {
      return;
    }

    if (peer.producers.has(producerId)) {
      return;
    }

    const producerData: ProducerData = {
      id: producerId,
      kind: 'audio',
      peerId,
      label: TrackLabel.Microphone,
      paused: false,
      encodings: DEFAULT_AUDIO_ENCODINGS,
      maxSpatialLayer: 0,
    };

    peer.observer.safeEmit(MEDIASOUP_EVENTS.newProducer, producerData);

    this.#logger.warn('Recovered missing audio producer from audio observer volume', {
      peerId,
      producerId,
    });
  }
}

export default ChannelAudioObserverMissingProducerRestorer;
